import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { Copy, Check, Send, KeyRound, Mail } from 'lucide-react'
import Layout from '../../components/Layout'
import { sortPeople } from '../../utils/peopleSort'
import { PayerBadge } from '../../components/billing/BillingUI'
import { BillingPresentationNotices } from '../../components/billing/BillingPresentation'
import type { BillingSubscription } from '../../types'

interface InvitationRow {
  id: number
  email: string
  role: string
  accept_url: string
  preview_text: string
  expires_at_display: string
}

interface DirectUserRow {
  id: number
  email: string
  full_name: string
  role: string
  temp_password: string | null
  preview_text: string | null
}

interface Props {
  tenant: { id: number; name: string; slug: string }
  invitations: InvitationRow[]
  direct_users: DirectUserRow[]
  sendgrid_active: boolean
  login_url: string
  billing_subscription: BillingSubscription | null
  billing_detail_path: string | null
}

const ROLE_LABELS: Record<string, string> = {
  observer: 'Observer',
  member: 'Member',
  admin: 'Admin',
  account_owner: 'Account Owner',
}

function CopyButton({ text, label = 'Copy email' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1.5 px-3 py-1.5 border border-stone/30 rounded-lg text-sm font-semibold text-text-primary hover:bg-stone/10"
    >
      {copied ? <Check className="w-4 h-4 text-status-win" /> : <Copy className="w-4 h-4" />}
      {copied ? 'Copied!' : label}
    </button>
  )
}

export default function GuideTenantsReview() {
  const { tenant, invitations = [], direct_users = [], sendgrid_active, billing_subscription, billing_detail_path } = usePage().props as unknown as Props
  const [sendingId, setSendingId] = useState<number | null>(null)
  const [sentIds, setSentIds] = useState<number[]>([])

  const sendInvitation = (invitationId: number) => {
    setSendingId(invitationId)
    router.post(`/guide_tenants/${tenant.id}/deliver_invitation`, { invitation_id: invitationId }, {
      onFinish: () => setSendingId(null),
      onSuccess: () => setSentIds(ids => [...ids, invitationId]),
      preserveScroll: true,
    })
  }

  return (
    <Layout>
      <Head title={`${tenant.name} created - Pinnacle`} />
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <Link href="/guide_dashboard" className="text-sm text-pinnacle-red hover:underline">&larr; All Accounts</Link>
          <h1 className="text-3xl font-bold text-text-primary font-heading mt-2">{tenant.name} is ready</h1>
          <p className="text-base text-text-muted mt-1">
            You're attached as this company's Guide. Get the invitations below into people's hands — copy the email content, or send it directly.
          </p>
        </div>

        <section className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-3">
          <h2 className="text-lg font-semibold text-text-primary">Billing handoff</h2>
          {billing_subscription ? <>
            <p className="text-base font-semibold text-text-primary">{billing_subscription.plan.name}{billing_subscription.plan.price_display ? ` · ${billing_subscription.plan.price_display}` : ''}</p>
            <PayerBadge payer={billing_subscription.payer} />
            <p className="text-sm text-text-muted">{billing_subscription.trial_decision === 'trial' || billing_subscription.trial_requested ? `${billing_subscription.trial_days ?? 'Configured'}-day trial starts after secure card setup.` : 'Billing starts immediately after secure card setup.'} Creating the company did not charge a card or start a trial.</p>
            <BillingPresentationNotices presentation={billing_subscription.presentation} />
            {billing_subscription.payer === 'guide' ? <>
              <p className="text-sm text-text-muted">You are responsible for payment setup. Open this company's Guide Billing details to continue to secure setup.</p>
              {billing_detail_path ? <Link href={billing_detail_path} className="inline-block px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold">Continue to Guide Billing details</Link> : <p className="text-sm text-text-muted">Billing details are not yet available. Check the portfolio for setup availability.</p>}
            </> : <p className="text-sm text-text-muted">Send the invitations below. A company Admin or Account Owner must accept their invitation or log in, then complete payment setup in Company Billing at <code>/admin/billing</code>. That is the client's route, not a Guide setup route.</p>}
          </> : <p className="text-sm text-text-muted">No billing terms were selected because new billing enrollment was disabled. No subscription, charge, or trial was started. Visit your portfolio to enroll when billing becomes available.</p>}
          <Link href="/guide_billing" className="inline-block text-sm font-semibold text-pinnacle-red hover:underline">View billing portfolio</Link>
        </section>

        {!sendgrid_active && (invitations.length > 0) && (
          <div className="px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
            System email isn't configured or is inactive, so direct sending is disabled. Use "Copy email" and send the content from your own email instead.
          </div>
        )}

        {invitations.length > 0 && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-5">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2"><Mail className="w-5 h-5" /> Invitations</h2>
            {sortPeople(invitations).map(inv => (
              <div key={inv.id} className="border border-stone/20 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{inv.email}</p>
                    <p className="text-xs text-text-muted">{ROLE_LABELS[inv.role] || inv.role} · Invitation expires {inv.expires_at_display}</p>
                  </div>
                  <div className="flex gap-2">
                    <CopyButton text={inv.preview_text} />
                    <button
                      onClick={() => sendInvitation(inv.id)}
                      disabled={!sendgrid_active || sendingId === inv.id}
                      title={sendgrid_active ? undefined : 'System email is not configured or inactive'}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sentIds.includes(inv.id) ? <Check className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                      {sendingId === inv.id ? 'Sending…' : sentIds.includes(inv.id) ? 'Sent' : 'Send via email'}
                    </button>
                  </div>
                </div>
                <pre className="text-xs text-text-primary bg-stone/5 border border-stone/10 rounded-lg p-3 whitespace-pre-wrap font-mono max-h-56 overflow-y-auto">{inv.preview_text}</pre>
              </div>
            ))}
          </div>
        )}

        {direct_users.length > 0 && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6 space-y-5">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2"><KeyRound className="w-5 h-5" /> Temporary password logins</h2>
            <p className="text-sm text-text-muted">
              These people log in with a temporary password and are required to choose their own password on first login. Temporary passwords are shown here <span className="font-semibold">only once</span> — copy them now.
            </p>
            {sortPeople(direct_users).map(u => (
              <div key={u.id} className="border border-stone/20 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{u.full_name} <span className="font-normal text-text-muted">({u.email})</span></p>
                    <p className="text-xs text-text-muted">{ROLE_LABELS[u.role] || u.role}</p>
                  </div>
                  {u.preview_text && <CopyButton text={u.preview_text} label="Copy credentials email" />}
                </div>
                {u.preview_text ? (
                  <pre className="text-xs text-text-primary bg-stone/5 border border-stone/10 rounded-lg p-3 whitespace-pre-wrap font-mono max-h-56 overflow-y-auto">{u.preview_text}</pre>
                ) : (
                  <p className="text-xs text-text-muted">The temporary password was only shown right after creation and can't be displayed again. If they lost it, use the password reset flow.</p>
                )}
              </div>
            ))}
          </div>
        )}

        {invitations.length === 0 && direct_users.length === 0 && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm p-6">
            <p className="text-sm text-text-muted">No pending invitations or new logins for this company. You can invite people any time from its People page.</p>
          </div>
        )}

        <div className="flex justify-end">
          <Link href="/guide_dashboard" className="px-4 py-2 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90">
            Done — back to All Accounts
          </Link>
        </div>
      </div>
    </Layout>
  )
}
